import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

// Stores y APIs
import useAuthStore from '@store/useAuthStore';
import { getGamesForPlayer, createPlayer, createGame } from '@/api/gameApi'; // Asegúrate que gameApi exista

export function useDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // --- 1. Usamos useAuthStore como única fuente de verdad ---
  const { user, players, activePlayer, setActivePlayer } = useAuthStore();

  // --- 2. Estados locales para la UI del Dashboard ---
  const [newPlayerName, setNewPlayerName] = useState('');
  const [dashboardMessage, setDashboardMessage] = useState(null);

  // --- 3. Efecto para manejar la selección automática de jugador ---
  useEffect(() => {
    // Si el usuario ya cargó y no tiene jugadores
    if (user && players.length === 0) {
      setDashboardMessage({ type: 'error', text: "No tienes jugadores creados para poder jugar." });
      return;
    }

    // Si hay jugadores pero ninguno está activo
    if (user && players.length > 0 && !activePlayer) {
      setDashboardMessage({ type: 'info', text: 'Seleccionando el primer jugador por defecto...' });
      
      const timer = setTimeout(() => {
        setActivePlayer(players[0].id);
        setDashboardMessage(null); // Limpiamos el mensaje después de la acción
      }, 1500); // Mostramos el mensaje por 1.5 segundos

      return () => clearTimeout(timer);
    }
  }, [user, players, activePlayer, setActivePlayer]);

  // --- 4. Queries y Mutations ---
  const { data: unsortedGames = [], error: gameError } = useQuery({
    queryKey: ['games', activePlayer?.id],
    queryFn: () => getGamesForPlayer(activePlayer.id),
    enabled: !!activePlayer,
  });

  // ✅ ORDENAMOS LOS JUEGOS AQUÍ
  // Creamos una copia con [...unsortedGames] para no mutar la caché de React Query
  // y ordenamos por 'id' de forma ascendente (el más bajo primero).
  const games = [...unsortedGames].sort((a, b) => a.id - b.id);

  const createPlayerMutation = useMutation({
    mutationFn: createPlayer,
    onSuccess: () => {
      setNewPlayerName('');
      queryClient.invalidateQueries({ queryKey: ['user'] }); // Invalida para que fetchUser se vuelva a llamar
    },
  });

  const createGameMutation = useMutation({
    mutationFn: createGame,
    onSuccess: (newGame) => {
      navigate(`/lobby/${newGame.id}`);
    },
  });

  // --- 5. Handlers ---
  const handleCreatePlayer = () => {
    if (newPlayerName) createPlayerMutation.mutate(newPlayerName);
  };

  const handleSelectPlayer = (e) => {
    const playerId = e.target.value;
    if (playerId) setActivePlayer(playerId);
  };

  const handleCreateGame = (gameData) => {
    createGameMutation.mutate(gameData);
  };

  // --- 6. Devolvemos todo lo que la vista necesita ---
  return {
    user,
    players,
    activePlayer,
    games,
    gameError,
    newPlayerName,
    setNewPlayerName,
    handleCreatePlayer,
    handleSelectPlayer,
    handleCreateGame,
    createPlayerMutation,
    createGameMutation,
    dashboardMessage,
  };
}